import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Card, Container, Dialog, Grid, Skeleton, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import ModeEditOutlineOutlinedIcon from '@mui/icons-material/ModeEditOutlineOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import SummarizeOutlinedIcon from '@mui/icons-material/SummarizeOutlined';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { validate } from '../../api/services/auth';
import { useNavigate } from 'react-router-dom';
import { pushNotification } from '../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider';
import { NotificationTypes } from '../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types';
import { handleApiError } from '../../utils/errorHandler';
import { ADMIN_CURRENT_NAME, ADMIN_STORAGE_KEY, NO_DATA_TO_DISPLAY_WITHOUT_TEXT } from '../../utils/constants';
import Sidebar from '../../layouts/Sidebar/Sidebar';
import Header from '../../layouts/Header/Header';
import PinnacleDrawer from '../../components/PinnacleDrawer/PinnacleDrawer';
import { getModifiedFieldsOnly } from '../../utils/getModifiedFieldsOnly';
import { FormData, useExamCodeForm } from './hooks/useExamCodeForm';
import PinnacleConfirmDeleteItem from '../../components/PinnacleConfirmDeleteItem/PinnacleConfirmDeleteItem';
import { PinnacleSearchField } from '../../components/PinnacleSearchField/PinnacleSearchField';
import { useDebounce } from 'use-debounce';
import { ExamCode } from '../../api/interfaces/preboard/ExamCode';
import { ExamByCodeResponse } from '../../api/interfaces/preboard/Exam';
import { createExamByCode, createExamCode, deleteExamByCode, deleteExamCode, getExamCodes, getExamsByCode, sortExamByCode, updateExamByCode, updateExamCode } from '../../api/services/preboard/exam';
import { SkeletonPreboardExamCode } from '../../components/skeleton/SkeletonPreboardExamCode/SkeletonPreboardExamCode';
import CreateExamCodeDrawerContent from './partials/exam-code/CreateExamCodeDrawerContent';
import EditExamCodeDrawerContent from './partials/exam-code/EditExamCodeDrawerContent';
import CreateExamByCodeDrawerContent from './partials/exam-by-code/CreateExamByCodeDrawerContent/CreateExamByCodeDrawerContent';
import { ExamByCodeFormData, useExamByCodeForm } from './hooks/useExamByCodeForm';
import { uploadFile } from '../../api/services/file';
import EditExamByCodeDrawerContent from './partials/exam-by-code/EditExamByCodeDrawerContent/EditExamByCodeDrawerContent';
import ExportExamDrawerContent from './partials/export-exam/ExportExamDrawerContent';
import { localToUTCISOString } from '../../utils/formatDate';
import { closestCenter, DndContext, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import SortableExamItem from './partials/SortableExamItem';

const PREBOARD_QUESTIONNAIRE_BUCKET = 'preboard-questionnaire';
const PREBOARD_QUESTIONNAIRE_KEYS_BUCKET = 'preboard-questionnaire-with-keys';

export const PREBOARD_EXAM_CODE_TABLE_COLUMNS = "290px 170px 180px 260px 40px";

const PreboardExamPage: React.FC = () => {
    const navigate = useNavigate();

    // Initial form data
    const initialFormData = React.useMemo<FormData>(() => ({
        name: '',
        description: '',
        code: '',
        startDate: '',
        endDate: '',
        showAnswersDate: '',
        isExclusive: false,
    }), []);

    // Initial touch fields for error validation
    const initialTouchFields = {
        name: false,
        description: false,
        code: false,
        startDate: false,
        endDate: false,
        showAnswersDate: false,
        isExclusive: false
    };

    // Hooks for form and validation states
    const {
        formData,
        rawFormData,
        setRawFormData,
        setFormData,
        touchedFields,
        setTouchedFields,
        handleChange,
        handleBlur,
        resetForm,
        isFormValid
    } = useExamCodeForm({
        initialFormData,
    });

    const [listViewType, setListViewType] = useState<'examCodes' | 'exams'>('examCodes');

    const [currentAdminName, setCurrentAdminName] = useState<string>('');
    const [examCodes, setExamCodes] = useState<ExamCode[]>([]);
    const [examsByCode, setExamsByCode] = useState<ExamByCodeResponse[]>([]);
    const [isExamCodesLoading, setIsExamCodesLoading] = useState<boolean>(false);
    const [isExamsByCodeLoading, setIsExamsByCodeLoading] = useState<boolean>(false);

    // Search exam codes with debounce
    const [searchExamCodes, setSearchExamCodes] = useState<string>('');
    const [debouncedSearchExamCodes] = useDebounce(searchExamCodes, 500);

    // Drawable states for exam code create and edit
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [currentDrawableState, setCurrentDrawableState] = useState<string>('');
    const [currentDrawableTitle, setCurrentDrawableTitle] = useState<string>('');

    const [selectedExamCodeId, setSelectedExamCodeId] = useState<string>('');
    const [selectedExamCodeName, setSelectedExamCodeName] = useState<string>('');

    // Accordion state 
    const [expanded, setExpanded] = useState<string | false>(false);

    // This will be used in the edit wherein we will identify the textfields
    // changes compare by comparing to this original form data
    const [originalFormData, setOriginalFormData] = useState(initialFormData);

    // Confirm delete dialog state
    const [isConfirmDeleteDialogOpen, setIsConfirmDeleteDialogOpen] = useState<boolean>(false);

    // Submit status state
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [sortableExams, setSortableExams] = useState<
        ExamByCodeResponse[]
    >([]);
    const [hasOrderChanges, setHasOrderChanges] = useState(false);

    const fetchExamCodes = useCallback(async (keyword?: string) => {
        try {
            const examCodesResponse = await getExamCodes({ keyword });
            setExamCodes(Array.isArray(examCodesResponse?.data?.data)
                ? examCodesResponse.data.data
                : []
            );
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        }
    }, []);

    const fetchExamsByCode = useCallback(async (examCodeId: string) => {
        try {
            const examCodesResponse = await getExamsByCode(examCodeId);
            setExamsByCode(examCodesResponse.data);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        }
    }, []);

    // Search only applies for exam codes only
    const fetchSearchResults = useCallback(async (searchTerm: string) => {
        try {
            setIsExamCodesLoading(true);
            await fetchExamCodes(searchTerm);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({ details: msg, type: NotificationTypes.Error });
                },
            });
            setIsExamCodesLoading(false);
        } finally {
            setIsExamCodesLoading(false);
        }
    }, [debouncedSearchExamCodes]);

    const handleAccordionChange = (panel: string) => async (_: React.SyntheticEvent, isExpanded: boolean) => {
        if (hasOrderChanges) {
            pushNotification({
                details:
                    "Please save or cancel the current exam ordering first.",
                type: NotificationTypes.Warning,
            });

            return;
        }
        if (isExamsByCodeLoading) return;
        setExpanded(isExpanded ? panel : false);
        if (isExpanded) {
            try {
                setIsExamsByCodeLoading(true);
                setExamsByCode([]);
                await fetchExamsByCode(panel);
            } catch (error) {
                handleApiError(error, {
                    notify: (msg) => {
                        pushNotification({
                            details: msg,
                            type: NotificationTypes.Error,
                        });
                    },
                });
                setIsExamsByCodeLoading(false);
                setExamsByCode([]);
            } finally {
                setIsExamsByCodeLoading(false);
            }
        }
    };

    // Handle the opening and closing of toggle
    const toggleDrawer = (open: boolean, state: 'create' | 'edit' | string, info?: string) => () => {
        if (open) {
            setCurrentDrawableState(state);
            setCurrentDrawableTitle(state === 'create' ? 'Add Exam Code' : state === 'edit' ? `Edit - ${info}` : state === 'export' ? 'Export Examination' : '');
        } else {
            if (state === 'edit') {
                resetForm();
            }
            setCurrentDrawableState('');
        }

        setIsDrawerOpen(open);
    };

    const handleCreateSubmit = async () => {
        setIsSubmitting(true);
        try {
            const utcFormData = {
                ...formData,
                startDate: localToUTCISOString(formData.startDate),
                endDate: localToUTCISOString(formData.endDate),
                showAnswersDate: localToUTCISOString(formData.showAnswersDate),
            };
            const createResponse = await createExamCode(utcFormData);
            setExamCodes((prevTerms) => [createResponse.data, ...prevTerms]);

            pushNotification({
                details: createResponse.message,
                type: NotificationTypes.Success,
            });
            toggleDrawer(false, '')();
            setRawFormData(initialFormData);
            setFormData(initialFormData);
            setTouchedFields(initialTouchFields);
            setCurrentDrawableState('');
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
            setIsSubmitting(false);
        } finally {
            setIsSubmitting(false);
        }
    };

    const modifiedFields = useMemo(
        () => getModifiedFieldsOnly(formData, originalFormData) || {},
        [formData, originalFormData]
    );

    const isFormModified = Object.keys(modifiedFields).length > 0;

    const handleEditExamCode = (selectedExamCode: ExamCode) => {
        const { _id, code, name, description, showAnswersDate, isExclusive, startDate, endDate } = selectedExamCode
        setSelectedExamCodeId(_id);
        setSelectedExamCodeName(name);

        const examCodeData: FormData = {
            code,
            name,
            description,
            showAnswersDate,
            isExclusive,
            startDate,
            endDate
        };
        setOriginalFormData(examCodeData as FormData);
        resetForm(examCodeData);
        toggleDrawer(true, 'edit', name)();
    };

    const handleUpdateSubmit = async () => {
        setIsSubmitting(true);
        try {
            const utcFormData = {
                ...modifiedFields
            };

            if (modifiedFields.startDate) {
                utcFormData.startDate = localToUTCISOString(formData.startDate);
            }

            if (modifiedFields.endDate) {
                utcFormData.endDate = localToUTCISOString(formData.endDate);
            }

            if (modifiedFields.showAnswersDate) {
                utcFormData.showAnswersDate = localToUTCISOString(formData.showAnswersDate);
            }

            const updateResponse = await updateExamCode(selectedExamCodeId, utcFormData);
            toggleDrawer(false, '')();
            setExamCodes((prev) =>
                prev.map((examCode) =>
                    examCode._id === updateResponse.data._id ? (updateResponse.data as ExamCode) : examCode
                )
            );

            pushNotification({
                details: updateResponse.message,
                type: NotificationTypes.Success,
            });
            setCurrentDrawableState('');
            setRawFormData(initialFormData);
            setFormData(initialFormData);
            setTouchedFields(initialTouchFields);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
            setIsSubmitting(false);
        } finally {
            setIsSubmitting(false);
        }
    }

    const handleDelete = (selectedExamCode: ExamCode) => {
        setSelectedExamCodeId(selectedExamCode._id);
        setSelectedExamCodeName(selectedExamCode.name)
        setIsConfirmDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        setIsSubmitting(true);
        try {
            const deleteResponse = await deleteExamCode(selectedExamCodeId);
            setExamCodes(prev => prev.filter(ref => ref._id !== selectedExamCodeId));
            pushNotification({
                details: deleteResponse.message,
                type: NotificationTypes.Success,
            });
            setIsConfirmDeleteDialogOpen(false);
            setSelectedExamCodeId('');
            setSelectedExamCodeName('');
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
            setIsSubmitting(false);
        } finally {
            setIsSubmitting(false);
        }
    }

    const handleDrawerClose = () => {
        toggleDrawer(false, currentDrawableState)()
        setRawFormData(initialFormData);
        setFormData(initialFormData);
        setTouchedFields(initialTouchFields);
        setCurrentDrawableState('');
        setIsSubmitting(false);
    };

    // ========================= EXAM BY EXAM CODE =========================
    // Drawable states for exam by exam code
    const [isExamByCodeDrawerOpen, setIsExamByCodeDrawerOpen] = useState<boolean>(false);
    const [currentExamByCodeDrawableState, setCurrentExamByCodeDrawableState] = useState<string>('');
    const [currentDrawableExamByCodeTitle, setCurrentDrawableExamByCodeTitle] = useState<string>('');
    const [selectedExamIdByCode, setSelectedExamIdByCode] = useState<string>('');

    const [selectedQuestionnaireFile, setSelectedQuestionnaireFile] = useState<File | null>(null);
    const fileQuestionnaireInputRef = useRef<HTMLInputElement>(null as unknown as HTMLInputElement);

    const [selectedQuestionnaireKeysFile, setSelectedQuestionnaireKeysFile] = useState<File | null>(null);
    const fileQuestionnaireKeysInputRef = useRef<HTMLInputElement>(null as unknown as HTMLInputElement);

    // Use the correct type for the ref to access handleSubmit
    const createEditExamByCodeRef = useRef<{ handleSubmit: () => void } | null>(null);

    // Confirm delete dialog state
    const [isConfirmDeleteExamByCodeDialogOpen, setIsConfirmDeleteExamByCodeDialogOpen] = useState<boolean>(false);

    // Initial form data
    const initialExamByCodeFormData = React.useMemo<ExamByCodeFormData>(() => ({
        name: '',
        description: '',
        startDate: '',
        maxAttempts: 0,
        timer: 0,
        rebaseValue: 0,
        questionnaire: '',
        questionnaireWithKeys: '',
        numberOfQuestions: 0,
        answerKey: []
    }), []);

    // Initial touch fields for error validation
    const initialExamByCodeTouchFields = {
        name: false,
        description: false,
        startDate: false,
        maxAttempts: false,
        timer: false,
        rebaseValue: false,
        questionnaire: false,
        questionnaireWithKeys: false,
        numberOfQuestions: false,
        answerKey: false
    };

    // This will be used in the edit wherein we will identify the textfields
    // changes compare by comparing to this original form data
    const [originalExamByCodeFormData, setOriginalExamByCodeFormData] = useState(initialExamByCodeFormData);

    // Hooks for form and validation states
    const {
        examByCodeFormData,
        rawExamByCodeFormData,
        setRawExamByCodeFormData,
        setExamByCodeFormData,
        examByCodeTouchedFields,
        setExamByCodeTouchedFields,
        handleExamByCodeChange,
        handleExamByCodeBlur,
        resetExamByCodeForm,
        isExamByCodeFormValid
    } = useExamByCodeForm({
        initialExamByCodeFormData,
    });

    const handleExamByCodeDrawerClose = () => {
        setIsExamByCodeDrawerOpen(false);
        setSelectedExamIdByCode('')
        setCurrentDrawableExamByCodeTitle('');
        setSelectedQuestionnaireFile(null);
        setSelectedQuestionnaireKeysFile(null);
        if (fileQuestionnaireInputRef.current) {
            fileQuestionnaireInputRef.current.value = '';
        }
        if (fileQuestionnaireKeysInputRef.current) {
            fileQuestionnaireKeysInputRef.current.value = '';
        }
        setRawExamByCodeFormData(initialExamByCodeFormData);
        setExamByCodeFormData(initialExamByCodeFormData);
    }

    const handleCreateExamByCode = (e: any, examCode: ExamCode) => {
        e.stopPropagation();
        setCurrentDrawableExamByCodeTitle(`Add Examination - ${examCode.name}`);
        setCurrentExamByCodeDrawableState('create-exam');
        setSelectedExamIdByCode(examCode._id)
        setIsExamByCodeDrawerOpen(true);
        setRawExamByCodeFormData(initialExamByCodeFormData);
        setExamByCodeFormData(initialExamByCodeFormData);
    }

    const handleCreateExamByCodeSubmit = async () => {
        setIsSubmitting(true);
        try {
            let questionnaire = examByCodeFormData.questionnaire;
            if (selectedQuestionnaireFile) {
                const questionnaireFile = selectedQuestionnaireFile;
                const uploadQuestionnaireResponse = await uploadFile(PREBOARD_QUESTIONNAIRE_BUCKET, questionnaireFile);
                questionnaire = uploadQuestionnaireResponse.data.location;

                setRawExamByCodeFormData((prev) => ({ ...prev, questionnaire }));
                setExamByCodeFormData((prev) => ({ ...prev, questionnaire }));
            }

            let questionnaireWithKeys = examByCodeFormData.questionnaireWithKeys;
            if (selectedQuestionnaireKeysFile) {
                const questionnaireKeysFile = selectedQuestionnaireKeysFile;
                const uploadQuestionnaireKeys = await uploadFile(PREBOARD_QUESTIONNAIRE_KEYS_BUCKET, questionnaireKeysFile);
                questionnaireWithKeys = uploadQuestionnaireKeys.data.location;

                setRawExamByCodeFormData((prev) => ({ ...prev, questionnaireWithKeys }));
                setExamByCodeFormData((prev) => ({ ...prev, questionnaireWithKeys }));
            }

            const utcFormData = {
                ...examByCodeFormData,
                questionnaire,
                questionnaireWithKeys,
                examCodeId: selectedExamIdByCode,
                startDate: localToUTCISOString(examByCodeFormData.startDate)
            };

            const createExamResponse = await createExamByCode(utcFormData);
            setExamsByCode(prev => {
                // Only update the currently expanded accordion
                if (expanded !== selectedExamIdByCode) {
                    return prev;
                }

                return [createExamResponse.data, ...prev];
            });
            // Recount parent table
            setExamCodes(prev =>
                prev.map(code =>
                    code._id === selectedExamIdByCode
                        ? {
                            ...code,
                            numberOfExams: (code.numberOfExams ?? 0) + 1,
                        }
                        : code
                )
            );

            pushNotification({
                details: createExamResponse.message,
                type: NotificationTypes.Success,
            });
            setRawExamByCodeFormData(initialExamByCodeFormData);
            setExamByCodeFormData(initialExamByCodeFormData);
            setExamByCodeTouchedFields(initialExamByCodeTouchFields);
            setCurrentDrawableExamByCodeTitle('');
            setCurrentExamByCodeDrawableState('');
            setSelectedExamIdByCode('')
            setIsExamByCodeDrawerOpen(false);
            setSelectedQuestionnaireFile(null);
            setSelectedQuestionnaireKeysFile(null);
            if (fileQuestionnaireInputRef.current) {
                fileQuestionnaireInputRef.current.value = '';
            }
            if (fileQuestionnaireKeysInputRef.current) {
                fileQuestionnaireKeysInputRef.current.value = '';
            }
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
            setIsSubmitting(false);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleUploadQuestionnaireClick = () => {
        fileQuestionnaireInputRef.current?.click();
    };

    const handleUploadQuestionnaireKeysClick = () => {
        fileQuestionnaireKeysInputRef.current?.click();
    }

    const handleFileQuestionnaireChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedQuestionnaireFile(file);
            setRawExamByCodeFormData((prev) => ({
                ...prev,
                questionnaire: 'selected',
            }));
        }
    };

    const handleFileQuestionnaireKeysChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedQuestionnaireKeysFile(file);
            setRawExamByCodeFormData((prev) => ({
                ...prev,
                questionnaireWithKeys: 'selected',
            }));
        }
    };

    // const isExamByCodeFormModified = Object.keys(modifiedExamByCodeFields).length > 0;

    const handleEditExamByCode = (selectedExam: ExamByCodeResponse) => {
        setSelectedExamCodeId(selectedExam.examCodeId);
        setSelectedExamCodeName(selectedExam.name);

        const examData: ExamByCodeFormData = {
            name: selectedExam.name,
            description: selectedExam.description,
            startDate: selectedExam.startDate,
            maxAttempts: selectedExam.maxAttempts,
            timer: selectedExam.timer,
            rebaseValue: selectedExam.rebaseValue,
            questionnaire: selectedExam.questionnaire,
            questionnaireWithKeys: selectedExam.questionnaireWithKeys,
            numberOfQuestions: selectedExam.numberOfQuestions,
            answerKey: selectedExam.answerKey
        };
        setOriginalExamByCodeFormData(examData);
        resetExamByCodeForm(examData);

        setCurrentDrawableExamByCodeTitle(`Edit - ${selectedExam.name}`);
        setCurrentExamByCodeDrawableState('edit-exam');
        setSelectedExamIdByCode(selectedExam._id)
        setIsExamByCodeDrawerOpen(true);
    }

    const handleUpdateExamByCode = async () => {
        setIsSubmitting(true);
        try {

            let questionnaire = examByCodeFormData.questionnaire;
            if (selectedQuestionnaireFile) {
                const questionnaireFile = selectedQuestionnaireFile;
                const uploadQuestionnaireResponse = await uploadFile(PREBOARD_QUESTIONNAIRE_BUCKET, questionnaireFile);
                questionnaire = uploadQuestionnaireResponse.data.location;

                setRawExamByCodeFormData((prev) => ({ ...prev, questionnaire }));
                setExamByCodeFormData((prev) => ({ ...prev, questionnaire }));
            }

            let questionnaireWithKeys = examByCodeFormData.questionnaireWithKeys;
            if (selectedQuestionnaireKeysFile) {
                const questionnaireKeysFile = selectedQuestionnaireKeysFile;
                const uploadQuestionnaireKeys = await uploadFile(PREBOARD_QUESTIONNAIRE_KEYS_BUCKET, questionnaireKeysFile);
                questionnaireWithKeys = uploadQuestionnaireKeys.data.location;

                setRawExamByCodeFormData((prev) => ({ ...prev, questionnaireWithKeys }));
                setExamByCodeFormData((prev) => ({ ...prev, questionnaireWithKeys }));
            }

            const payload = {
                ...examByCodeFormData,
                questionnaire,
                questionnaireWithKeys
            };

            if (examByCodeFormData.startDate) {
                payload.startDate = localToUTCISOString(examByCodeFormData.startDate);
            }

            const updateExamResponse = await updateExamByCode(selectedExamIdByCode, payload);
            setExamsByCode(prev =>
                prev.map(exam =>
                    exam._id === selectedExamIdByCode ? updateExamResponse.data : exam
                )
            );

            pushNotification({
                details: updateExamResponse.message,
                type: NotificationTypes.Success,
            });
            setRawExamByCodeFormData(initialExamByCodeFormData);
            setExamByCodeFormData(initialExamByCodeFormData);
            setExamByCodeTouchedFields(initialExamByCodeTouchFields);
            setCurrentDrawableExamByCodeTitle('');
            setCurrentExamByCodeDrawableState('');
            setSelectedExamIdByCode('')
            setIsExamByCodeDrawerOpen(false);
            setSelectedQuestionnaireFile(null);
            setSelectedQuestionnaireKeysFile(null);
            if (fileQuestionnaireInputRef.current) {
                fileQuestionnaireInputRef.current.value = '';
            }
            if (fileQuestionnaireKeysInputRef.current) {
                fileQuestionnaireKeysInputRef.current.value = '';
            }
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        } finally {
            setIsSubmitting(false);
        }
    }

    const handleDeleteExamByCode = (selectedExam: ExamByCodeResponse) => {
        setSelectedExamCodeId(selectedExam.examCodeId);
        setSelectedExamIdByCode(selectedExam._id);
        setSelectedExamCodeName(selectedExam.name);
        setIsConfirmDeleteExamByCodeDialogOpen(true);
    };

    const handleConfirmExamByCodeDelete = async () => {
        setIsSubmitting(true);
        try {
            const deleteResponse = await deleteExamByCode(selectedExamIdByCode);
            setExamsByCode(prev => prev.filter(ref => ref._id !== selectedExamIdByCode));

            setExamCodes(prev =>
                prev.map(code =>
                    code._id === selectedExamCodeId
                        ? {
                            ...code,
                            numberOfExams: Math.max((code.numberOfExams ?? 1) - 1, 0),
                        }
                        : code
                )
            );

            pushNotification({
                details: deleteResponse.message,
                type: NotificationTypes.Success,
            });
            setSelectedExamIdByCode('');
            setSelectedExamCodeName('');
            setIsConfirmDeleteExamByCodeDialogOpen(false);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
            setIsSubmitting(false);
        } finally {
            setIsSubmitting(false);
        }
    }

    const handleExportExams = () => {
        setCurrentDrawableExamByCodeTitle('Export Examination');
        setCurrentExamByCodeDrawableState('export-exam');
        setIsExamByCodeDrawerOpen(true);
    }

    const handleSuccessExport = () => {
        setCurrentDrawableExamByCodeTitle('');
        setCurrentExamByCodeDrawableState('');
        setIsExamByCodeDrawerOpen(false);
        pushNotification({
            details: "Export completed successfully.",
            type: NotificationTypes.Success,
        });
    }

    const handleDragEnd = (
        event: DragEndEvent
    ) => {

        const { active, over } = event;

        if (!over) return;

        if (active.id === over.id) return;

        setSortableExams((items) => {

            const oldIndex =
                items.findIndex(
                    item => item._id === active.id
                );

            const newIndex =
                items.findIndex(
                    item => item._id === over.id
                );

            return arrayMove(
                items,
                oldIndex,
                newIndex
            );
        });

        setHasOrderChanges(true);
    };

    const handleSaveSortOrder = async () => {
        try {

            const payload = {
                examCodeId: expanded,
                exams: sortableExams.map(
                    (exam) => exam._id
                )
            };
            console.log(payload);

            const sortExamByCodeResponse = await sortExamByCode(payload);

            setExamsByCode(sortableExams);

            setHasOrderChanges(false);

            pushNotification({
                details: sortExamByCodeResponse.message,
                type: NotificationTypes.Success
            });

        } catch (error) {

            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        }
    };

    const handleCancelSortChanges = () => {
        setSortableExams(examsByCode);
        setHasOrderChanges(false);
    };

    // Initial state when component load
    useEffect(() => {
        const run = async () => {
            setIsExamCodesLoading(true);
            const token =
                localStorage.getItem(ADMIN_STORAGE_KEY) ||
                sessionStorage.getItem(ADMIN_STORAGE_KEY);
            if (!token) {
                navigate('/login');
                return;
            }
            const cachedName = localStorage.getItem(ADMIN_CURRENT_NAME);
            if (cachedName) {
                setCurrentAdminName(cachedName);
            }
            try {
                const validateResponse = await validate();
                const firstName = validateResponse.data.firstName;
                if (!cachedName) {
                    localStorage.setItem(ADMIN_CURRENT_NAME, firstName);
                }
                setCurrentAdminName(firstName);
                await fetchExamCodes();
            } catch (error) {
                handleApiError(error, {
                    notify: (msg) => {
                        pushNotification({
                            details: msg,
                            type: NotificationTypes.Error,
                        });
                    },
                });
                setIsExamCodesLoading(false);
            } finally {
                setIsExamCodesLoading(false);
            }
        };
        run();
    }, [fetchExamCodes]);

    // Set sortable exams when exams by code changes for drag and drop functionality
    useEffect(() => {
        setSortableExams(examsByCode);
    }, [examsByCode]);

    // Debouncing mechanism for search reference
    useEffect(() => {
        const run = async () => {
            setListViewType('examCodes');
            setIsExamCodesLoading(true);
            setExamCodes([]);

            try {
                if (debouncedSearchExamCodes.trim()) {
                    await fetchExamCodes(debouncedSearchExamCodes.trim());
                } else {
                    // 🔥 THIS IS THE FIX: refetch all when cleared
                    await fetchExamCodes();
                }
            } finally {
                setIsExamCodesLoading(false);
            }
        };

        run();
    }, [debouncedSearchExamCodes, fetchExamCodes]);

    const NoDataToDisplay = () => {
        return (
            <Box textAlign="center" py={4} color="#818181" sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center"
            }}>
                <Box
                    component="img"
                    src={NO_DATA_TO_DISPLAY_WITHOUT_TEXT}
                    sx={{ width: 200, mb: 1 }}
                />
                <Typography variant="caption">
                    No data to display
                </Typography>
            </Box>
        )
    }

    return (
        <main className="main-content">
            <Sidebar />

            <Box
                component="section"
                sx={{
                    flexGrow: 1,
                    width: '100%',
                    overflowX: 'hidden'
                }}
            >
                <Container
                    maxWidth="lg"
                    sx={{
                        mt: { xs: 4, sm: 6 },
                        mb: 4,
                        px: { xs: 2, sm: 3 },
                    }}
                >
                    <Header title='Videos' currentAdminName={currentAdminName} />

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: 4,
                            flexWrap: 'wrap',
                            gap: '2'
                        }}
                    >
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            sx={{ mr: 2 }}
                            onClick={toggleDrawer(true, 'create')}
                            disabled={isExamCodesLoading || isExamsByCodeLoading || hasOrderChanges}
                        >
                            Exam Code
                        </Button>

                        <PinnacleSearchField
                            id="searchExamCode"
                            label="Search by exam name or exam code"
                            value={searchExamCodes}
                            onChange={setSearchExamCodes}
                            isLoading={isExamCodesLoading}
                            disabled={hasOrderChanges}
                        />
                    </Box>

                    <Card
                        elevation={0}
                        sx={{
                            borderRadius: '8px',
                            p: 3,
                            backgroundColor: '#fff',
                            boxShadow: '0px 4px 12px 8px rgba(211, 211, 211, 0.2)',
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            height: 'auto',
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'left', gap: 2, alignItems: 'center', pb: 3 }}>
                            <Typography variant="body1" fontWeight={600}>
                                Exam Management
                            </Typography>

                            <Button
                                variant="contained"
                                className="btn-white"
                                startIcon={<SummarizeOutlinedIcon />}
                                onClick={handleExportExams}
                                disabled={isSubmitting || hasOrderChanges}
                                size="small"
                            >
                                Export
                            </Button>
                        </Box>
                        <Grid container rowSpacing={1} columnSpacing={1}>
                            {isExamCodesLoading ? (
                                <SkeletonPreboardExamCode />
                            ) : (
                                <Grid size={12}>
                                    <Box
                                        sx={{
                                            overflowX: "auto",
                                            width: "100%",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "grid",
                                                gridTemplateColumns: PREBOARD_EXAM_CODE_TABLE_COLUMNS,
                                                minWidth: "950px",
                                                px: 2,
                                                py: 1.5,
                                                border: "1px solid #ddd",
                                                backgroundColor: "#5840BA",
                                                color: "white",
                                                fontWeight: 600,
                                            }}
                                        >
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>EXAM NAME</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>EXAM CODE</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>NO. OF EXAMS</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>ACTION</Typography>
                                            <Box />
                                        </Box>


                                        {isExamCodesLoading ? (
                                            Array.from({ length: 1 }).map((_, idx) => (
                                                <Skeleton
                                                    key={idx}
                                                    variant="rectangular"
                                                    height={50}
                                                    sx={{ borderRadius: 1, mb: 1 }}
                                                />
                                            ))
                                        ) : examCodes.length === 0 ? (
                                            <NoDataToDisplay />
                                        ) : (
                                            examCodes.map((examCode: ExamCode, index: number) => (
                                                <Accordion
                                                    key={examCode._id}
                                                    expanded={expanded === examCode._id}
                                                    onChange={handleAccordionChange(examCode._id)}
                                                    disableGutters
                                                    elevation={0}
                                                    square
                                                    sx={{
                                                        minWidth: "950px",
                                                        borderBottom: "1px solid #eee",
                                                        "&:before": { display: "none" },
                                                        backgroundColor: "transparent",
                                                        boxShadow: "none",
                                                    }}
                                                >
                                                    <AccordionSummary
                                                        expandIcon={
                                                            expanded === examCode._id ? (
                                                                <RemoveCircleIcon sx={{ color: "#003367", fontSize: 20 }} />
                                                            ) : (
                                                                <AddCircleIcon sx={{ color: "#003367", fontSize: 20 }} />
                                                            )
                                                        }
                                                        sx={{
                                                            gap: 1,
                                                            px: 1.5,
                                                            py: 1.75,
                                                            minHeight: 56,
                                                            backgroundColor: index % 2 === 0 ? "#F6F6F6" : "#FFFFFF",
                                                            "&:hover": {
                                                                backgroundColor: index % 2 === 0 ? "#ECECEC" : "#F9F9F9",
                                                            },

                                                            "& .MuiAccordionSummary-content": {
                                                                margin: 0,
                                                                display: "grid",
                                                                gridTemplateColumns: "300px 200px 150px 260px",
                                                                alignItems: "center",
                                                            },

                                                            "& .MuiAccordionSummary-expandIconWrapper": {
                                                                marginLeft: 0,
                                                                padding: 0,
                                                            },
                                                            color: "#373737"
                                                        }}

                                                    >
                                                        {/* EXAM NAME */}
                                                        <Typography variant="body2" fontWeight={400}>
                                                            {examCode.name}
                                                        </Typography>

                                                        {/* EXAM CODE */}
                                                        <Typography variant="body2" color="text.secondary">
                                                            {examCode.code}
                                                        </Typography>

                                                        {/* NO. OF EXAMS */}
                                                        <Typography variant="body2">
                                                            {examCode.numberOfExams ?? 0}
                                                        </Typography>

                                                        {/* ACTIONS */}
                                                        <Box sx={{ display: "flex", gap: 1 }}>
                                                            <Button
                                                                component="div"
                                                                role="button"
                                                                size="small"
                                                                variant="contained"
                                                                startIcon={<AddIcon />}
                                                                sx={{
                                                                    whiteSpace: "nowrap",
                                                                    minWidth: "auto",
                                                                    paddingX: 1.5,
                                                                    height: "24px",
                                                                }}
                                                                onClick={(e) => handleCreateExamByCode(e, examCode)}
                                                                disabled={hasOrderChanges}
                                                            >
                                                                Add Exam
                                                            </Button>

                                                            <Button
                                                                component="div"
                                                                role="button"
                                                                variant="outlined"
                                                                startIcon={<ModeEditOutlineOutlinedIcon />}
                                                                size="small"
                                                                sx={{
                                                                    border: "1px solid #003b71",
                                                                    height: "24px",
                                                                    width: "auto",
                                                                    fontSize: "12px"
                                                                }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleEditExamCode(examCode);
                                                                }}
                                                                disabled={hasOrderChanges}
                                                            >
                                                                edit
                                                            </Button>
                                                            <Button
                                                                component="div"
                                                                role="button"
                                                                variant="outlined"
                                                                startIcon={<DeleteOutlineOutlinedIcon />}
                                                                size="small"
                                                                color="inherit"
                                                                sx={{
                                                                    height: "24px",
                                                                    width: "auto",
                                                                    fontSize: "12px"
                                                                }}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleDelete(examCode);
                                                                }}
                                                                disabled={hasOrderChanges}
                                                            >
                                                                delete
                                                            </Button>
                                                        </Box>
                                                    </AccordionSummary>
                                                    <AccordionDetails>
                                                        {isExamsByCodeLoading ? (
                                                            Array.from({ length: 3 }).map((_, idx) => (
                                                                <Skeleton
                                                                    key={idx}
                                                                    variant="rectangular"
                                                                    height={50}
                                                                    sx={{ borderRadius: 1, mb: 1 }}
                                                                />
                                                            ))
                                                        ) : examsByCode && examsByCode.length > 0 ? (
                                                            <Box
                                                                sx={{
                                                                    maxHeight: 600,
                                                                    overflowY: "auto",
                                                                    pr: 1,
                                                                    "&::-webkit-scrollbar": {
                                                                        width: "6px",
                                                                    },
                                                                    "&::-webkit-scrollbar-thumb": {
                                                                        backgroundColor: "#ccc",
                                                                        borderRadius: "4px",
                                                                    },
                                                                }}
                                                            >
                                                                <DndContext
                                                                    collisionDetection={closestCenter}
                                                                    onDragEnd={
                                                                        isExamsByCodeLoading
                                                                            ? undefined
                                                                            : handleDragEnd
                                                                    }
                                                                >
                                                                    <SortableContext
                                                                        items={sortableExams.map(x => x._id)}
                                                                        strategy={verticalListSortingStrategy}
                                                                    >
                                                                        {sortableExams.map((exam) => (
                                                                            <SortableExamItem
                                                                                key={exam._id}
                                                                                exam={exam}
                                                                                onEdit={() => handleEditExamByCode(exam)}
                                                                                onDelete={() => handleDeleteExamByCode(exam)}
                                                                                isSorting={hasOrderChanges}
                                                                            />
                                                                        ))}
                                                                    </SortableContext>
                                                                </DndContext>
                                                                {hasOrderChanges && (
                                                                    <Box
                                                                        sx={{
                                                                            display: "flex",
                                                                            justifyContent: "flex-end",
                                                                            gap: 2,
                                                                            mt: 2,
                                                                            pt: 2,
                                                                            borderTop:
                                                                                "1px solid #eee"
                                                                        }}
                                                                    >
                                                                        <Button
                                                                            variant="outlined"
                                                                            onClick={
                                                                                handleCancelSortChanges
                                                                            }
                                                                        >
                                                                            Cancel
                                                                        </Button>

                                                                        <Button
                                                                            variant="contained"
                                                                            onClick={
                                                                                handleSaveSortOrder
                                                                            }
                                                                        >
                                                                            Save Order
                                                                        </Button>
                                                                    </Box>
                                                                )}
                                                            </Box>
                                                        ) : (
                                                            <NoDataToDisplay />
                                                        )}

                                                    </AccordionDetails>
                                                </Accordion>
                                            ))
                                        )}
                                    </Box>
                                </Grid>
                            )}
                        </Grid>
                    </Card>
                </Container>
            </Box>

            {/* EXAM CODES CREATE + EDIT */}
            <PinnacleDrawer
                title={currentDrawableTitle}
                isDrawerOpen={isDrawerOpen}
                onClose={() => handleDrawerClose()}
            >

                {currentDrawableState === 'create' && (
                    <CreateExamCodeDrawerContent
                        formData={rawFormData}
                        touchedFields={touchedFields}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onSubmit={handleCreateSubmit}
                        isSubmitting={isSubmitting}
                        isFormValid={isFormValid}
                    />
                )}

                {currentDrawableState === 'edit' && (
                    <EditExamCodeDrawerContent
                        formData={rawFormData}
                        touchedFields={touchedFields}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        onSubmit={handleUpdateSubmit}
                        isSubmitting={isSubmitting}
                        isFormValid={isFormValid}
                        isFormModified={isFormModified}
                    />
                )}
            </PinnacleDrawer>

            {/* CREATE + EDIT EXAM PER EXAM CODE */}
            <PinnacleDrawer
                title={currentDrawableExamByCodeTitle}
                isDrawerOpen={isExamByCodeDrawerOpen}
                onClose={() => handleExamByCodeDrawerClose()}
                customWidthSize={currentExamByCodeDrawableState === 'export-exam' ? 500 : 700}
                onSubmit={async () => {
                    if (createEditExamByCodeRef.current?.handleSubmit) {
                        await createEditExamByCodeRef.current.handleSubmit();
                    }
                }}
                onSubmitName={currentExamByCodeDrawableState === 'edit-exam' ? 'UPDATE INFORMATION' : currentExamByCodeDrawableState === 'create-exam' ? 'SAVE CHANGES' : ''}
                isSubmitting={isSubmitting}
            >
                {currentExamByCodeDrawableState === 'create-exam' && (
                    <CreateExamByCodeDrawerContent
                        ref={createEditExamByCodeRef}
                        formData={rawExamByCodeFormData}
                        rawFormData={rawExamByCodeFormData}
                        touchedFields={examByCodeTouchedFields}
                        onChange={handleExamByCodeChange}
                        onBlur={handleExamByCodeBlur}
                        onSubmit={handleCreateExamByCodeSubmit}
                        isSubmitting={isSubmitting}
                        isFormValid={isExamByCodeFormValid}
                        fileQuestionnaireInputRef={fileQuestionnaireInputRef}
                        handleUploadQuestionnaireClick={handleUploadQuestionnaireClick}
                        handleFileQuestionnaireChange={handleFileQuestionnaireChange}
                        selectedQuestionnaireFile={selectedQuestionnaireFile}
                        handleUploadQuestionnaireKeysClick={handleUploadQuestionnaireKeysClick}
                        fileQuestionnaireKeysInputRef={fileQuestionnaireKeysInputRef}
                        handleFileQuestionnaireKeysChange={handleFileQuestionnaireKeysChange}
                        selectedQuestionnaireKeysFile={selectedQuestionnaireKeysFile}
                        closeToggle={toggleDrawer(false, '')}
                        setIsSubmitting={setIsSubmitting}
                    />
                )}

                {currentExamByCodeDrawableState === 'edit-exam' && (
                    <EditExamByCodeDrawerContent
                        ref={createEditExamByCodeRef}
                        formData={rawExamByCodeFormData}
                        rawFormData={rawExamByCodeFormData}
                        touchedFields={examByCodeTouchedFields}
                        onChange={handleExamByCodeChange}
                        onBlur={handleExamByCodeBlur}
                        onSubmit={handleUpdateExamByCode}
                        isSubmitting={isSubmitting}
                        isFormValid={isExamByCodeFormValid}
                        fileQuestionnaireInputRef={fileQuestionnaireInputRef}
                        handleUploadQuestionnaireClick={handleUploadQuestionnaireClick}
                        handleFileQuestionnaireChange={handleFileQuestionnaireChange}
                        selectedQuestionnaireFile={selectedQuestionnaireFile}
                        handleUploadQuestionnaireKeysClick={handleUploadQuestionnaireKeysClick}
                        fileQuestionnaireKeysInputRef={fileQuestionnaireKeysInputRef}
                        handleFileQuestionnaireKeysChange={handleFileQuestionnaireKeysChange}
                        selectedQuestionnaireKeysFile={selectedQuestionnaireKeysFile}
                        closeToggle={toggleDrawer(false, '')}
                        setIsSubmitting={setIsSubmitting}
                    />
                )}

                {currentExamByCodeDrawableState === 'export-exam' && (
                    <ExportExamDrawerContent
                        onSubmit={handleSuccessExport}
                        isSubmitting={isSubmitting}
                        setIsSubmitting={setIsSubmitting}
                        examCodes={examCodes}
                    />
                )}

            </PinnacleDrawer>


            <Dialog
                open={isConfirmDeleteDialogOpen}
                onClose={() => setIsConfirmDeleteDialogOpen(!isConfirmDeleteDialogOpen)}
                aria-labelledby="responsive-dialog-title"
                sx={{
                    borderRadius: '0 !important'
                }}
            >
                <PinnacleConfirmDeleteItem
                    title="Delete Exam Code"
                    itemToDelete={selectedExamCodeName}
                    onClose={() => setIsConfirmDeleteDialogOpen(false)}
                    onDelete={handleConfirmDelete}
                    onCancel={() => setIsConfirmDeleteDialogOpen(false)}
                    isDeleting={isSubmitting}
                />
            </Dialog>

            <Dialog
                open={isConfirmDeleteExamByCodeDialogOpen}
                onClose={() => setIsConfirmDeleteExamByCodeDialogOpen(!isConfirmDeleteExamByCodeDialogOpen)}
                aria-labelledby="responsive-dialog-title"
                sx={{
                    borderRadius: '0 !important'
                }}
            >
                <PinnacleConfirmDeleteItem
                    title="Delete Exam"
                    itemToDelete={selectedExamCodeName}
                    onClose={() => setIsConfirmDeleteExamByCodeDialogOpen(false)}
                    onDelete={handleConfirmExamByCodeDelete}
                    onCancel={() => setIsConfirmDeleteExamByCodeDialogOpen(false)}
                    isDeleting={isSubmitting}
                />
            </Dialog>
        </main>
    );
};

export default PreboardExamPage;
